import {WriteButton} from '../common/buttons/WriteActions';
import React, {useEffect, useState} from "react";
import {decorationAPI} from "../../services/DecorationService";
import {DecorationGuidDto} from '../../models/Decorations/IDecorationGuid';
import {Box, InputAdornment, Paper, TextField} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MySnackBar from "../common/snackBars/MySnackBar";
import {DecorationTable} from "./decoration/DecorationTable";
import {useNavigate} from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import {Clear} from "@mui/icons-material";
import useDebounce from "../../hooks/useDebounce";
import {usePageLimit} from "../../hooks/usePageLimit";
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {getApiErrorMessage} from '../../common/apiErrors';
import {ContentSkeleton} from '../common/loading/ContentSkeleton';

type SearchPropsType = {
    page: number;
    pageSize: number;
    searchPhrase: string;
    descending: boolean;
};

interface ApiResponse {
    totalCount: number;
    pageCount: number;
    page: number;
    pageSize: number;
    data: DecorationGuidDto[];
}

function Decorations() {
    const {t} = useAppTranslation();
    const [page, setPage] = useState(0);
    const [limit, setLimit] = usePageLimit('decorations');
    const [searchPhrase, setSearchPhrase] = useState('');
    const debouncedSearchPhrase = useDebounce(searchPhrase, 600);
    const [descending, setDescending] = useState(false);
    const [orderByName, setOrderByName] = useState(true);
    const [orderByPrice, setOrderByPrice] = useState(false);
    const navigate = useNavigate();

    const {
        data: response,
        error,
        isLoading,
        isSuccess,
    } = decorationAPI.useGetDecorationsV2Query({
        page,
        pageSize: limit,
        searchPhrase: debouncedSearchPhrase.trim().length >= 3
            ? debouncedSearchPhrase.trim()
            : '',
        descending,
    } as SearchPropsType)

    const handleCloseSnackBar = () => setSnackBarOpen(false);
    const [openSnackBar, setSnackBarOpen] = useState(false);
    const [snackBarSeverity, setSnackBarSeverity] = useState<'success' | 'error'>('success');
    const [snackBarText, setSnackBarText] = useState('');

    useEffect(() => {
        if (isSuccess && response) {
            setSnackBarOpen(true);
            setSnackBarText(t('recipes:details_loaded'));
            setSnackBarSeverity('success');
        }
        if (error) {
            setSnackBarOpen(true);
            setSnackBarText(getApiErrorMessage(error, t));
            setSnackBarSeverity('error');
        }
    }, [isSuccess, error, response]);

    const handlePageChange = (newPage: number) => setPage(newPage);

    const handlePageSizeChange = (newSize: number) => {
        setLimit(newSize);
        setPage(0);
    };

    const handleSortRequest = (
        id: 'name' | 'price',
        direction: 'asc' | 'desc'
    ) => {
        setDescending(direction === 'desc');

        // Сбрасываем все флаги и включаем только нужный
        setOrderByName(id === 'name');
        setOrderByPrice(id === 'price');
    };

    return (<Box sx={{width: '100%'}}>
        <Box sx={{mb: 2, display: 'flex', justifyContent: 'flex-center', alignItems: 'center'}}>
            <WriteButton
                variant="contained"
                startIcon={<AddIcon/>}
                onClick={() => navigate('new')}
            >
                        {t('recipes:create_decoration')}
            </WriteButton>
        </Box>

        {isLoading && (
            <ContentSkeleton variant="list"/>
        )}

        {isSuccess && response && (
            <Paper sx={{width: '100%', overflow: 'hidden'}}>
                <Box sx={{p: 2}}>
                    <TextField
                        label={t('recipes:search_decorations')}
                        variant="outlined"
                        focused
                        size="small"
                        fullWidth
                        value={searchPhrase}
                        onChange={(e) => {
                            setSearchPhrase(e.target.value);
                        }}
                        InputProps={{
                            endAdornment: searchPhrase ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setSearchPhrase("");
                                        }}
                                    >
                                        <Clear/>
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                </Box>
                <DecorationTable
                    data={response.data}
                    totalCount={response.totalCount}
                    pageSize={limit}
                    currentPage={page}
                    descending={descending}
                    orderByName={orderByName}
                    orderByPrice={orderByPrice}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onSortRequest={handleSortRequest}
                />
            </Paper>)}

        <MySnackBar
            duration={5000}
            openSnackBar={openSnackBar}
            handleClose={handleCloseSnackBar}
            severity={snackBarSeverity}
            alertText={snackBarText}
        />
    </Box>)
}

export default Decorations;
